import { AisMapContext } from './ais-map.context';
import { ProviderCardActions } from './vessel.types';

/**
 * The contract a pluggable map feature implements (phase 3 scaffolding,
 * REFACTOR-PLAN.md §4.3). A feature is one file under `ais/shared/features/`
 * plus one line in the facade's feature array; its provider-specific I/O lives
 * behind a gateway in the provider's folder.
 *
 * ABSTRACT CLASS, NOT INTERFACE — deliberately. `tsconfig.json` sets
 * `noImplicitOverride: true`, so with the convention of always writing
 * `override`, a typo'd hook name is a compile error. With an all-optional
 * interface it is a method that silently never fires — the failure mode this
 * feature family has already shipped twice (the dead port-call button, the
 * dead radar button).
 *
 * Every hook is a no-op by default: a feature overrides only what it needs.
 *
 * The three contribution points were found in the code, not invented:
 * `attach()` is what the monolith's 127-line wiring block does per feature;
 * `syncCredits` is the "future third priced control gets kept in step by
 * adding a line here" its own doc asks for; `popupActions` is the merge
 * `popupOptsFor` already performs across four concerns.
 */
export abstract class AisMapFeature {
  constructor(protected readonly ctx: AisMapContext) {}

  /** Wire this feature's toolbar callbacks. Once, from the host constructor. */
  attach(): void {}

  /**
   * Drop what this feature owns on a task switch. FIELDS ONLY — the shared
   * sweep has already taken the layers off the map, so a `removeLayer` here
   * would operate on a map that no longer holds them (invariant I1).
   */
  reset(): void {}

  /**
   * Balance moved, prices moved, or the task did. Push into whatever
   * controller this feature owns. The broadcast is uniform; the handling is
   * per-feature — each priced controller has its own setter signature, and
   * that is the honest factoring (§4.3).
   */
  syncCredits(remaining: number | null, spendingClosed: boolean): void {}

  /**
   * This feature's buttons on the provider's vessel card, merged across
   * features by the facade. Return only your own keys: the merge is last-wins,
   * so a clashing key would silently clobber another feature's button (kept
   * disjoint by construction — see §10). A null price hides a button
   * (invariant I5); contributing nothing renders nothing.
   *
   * `Record<string, unknown>` since D15, not a shared named shape. The KEYS are
   * the provider's vocabulary — `portCallCredits` means something to Voyage
   * Radar's card builder and to nothing else — and the card host merges them
   * without reading them. A provider that wants its features type-checked
   * against its own card declares its own interface and narrows there
   * (`VoyagePopupActions`); what it must NOT do is add a key to a file every
   * other provider compiles against, which is what this contract used to
   * require and what D6 was open about.
   */
  popupActions(mmsi: string): ProviderCardActions {
    return {};
  }

  /** The live vessel layer was replaced. */
  onLiveLayer(layer: any, features: any[]): void {}

  /** The map selection changed (null = deselected). */
  onActive(vessel: { mmsi: string } | null): void {}
}
